import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Typography, message, Tag, Spin, Select, Button } from 'antd';
import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { liveFollowAPI, tradeAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const getDiffLines = (record) => {
  const diffLines = record?.diff_lines;
  if (!diffLines) return '';

  try {
    const parsed = typeof diffLines === 'string' ? JSON.parse(diffLines) : diffLines;
    if (!Array.isArray(parsed) || parsed.length === 0) return '';

    return parsed
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return JSON.stringify(item);
        }
        return String(item);
      })
      .join('\n');
  } catch (e) {
    return String(diffLines);
  }
};

const TradeRecords = () => {
  const [tradeRecords, setTradeRecords] = useState([]);
  const [follows, setFollows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFollow, setSelectedFollow] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchFollows = useCallback(async () => {
    try {
      const response = await tradeAPI.getLiveFollows();
      const list = response.data || [];
      setFollows(list);
      if (!selectedFollow && list.length > 0 && list[0] && list[0].id) {
        setLoading(true);
        setSelectedFollow(list[0].id);
      }
    } catch (error) {
      console.error('获取实盘列表失败:', error);
    }
  }, [selectedFollow]);

  const fetchTradeRecords = useCallback(async () => {
    try {
      if (!selectedFollow) {
        setTradeRecords([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        return;
      }
      // 获取总数据量用于分页
      const response = await liveFollowAPI.getJobRecords(selectedFollow, 1000, 0);
      const allData = response.data || [];
      const total = allData.length;

      // 使用函数式更新获取当前分页状态
      setPagination((prev) => {
        const startIndex = (prev.current - 1) * prev.pageSize;
        const endIndex = startIndex + prev.pageSize;
        const currentPageData = allData.slice(startIndex, endIndex);
        setTradeRecords(currentPageData);
        return { ...prev, total };
      });
    } catch (error) {
      message.error('获取交易记录失败');
      console.error('获取交易记录失败:', error);
      setTradeRecords([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [selectedFollow]);

  useEffect(() => {
    fetchFollows();
  }, [fetchFollows]);

  useEffect(() => {
    if (selectedFollow) {
      setLoading(true);
      fetchTradeRecords();
    }
  }, [fetchTradeRecords, selectedFollow]);

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const handleFollowChange = (value) => {
    setLoading(true);
    setSelectedFollow(value);
    // 切换实盘时重置到第一页
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    setLoading(true);
    // 刷新时重置到第一页
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchTradeRecords();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'diff_lines',
      dataIndex: 'diff_lines',
      key: 'diff_lines',
      render: (v, record) => {
        const diffText = getDiffLines(record);
        return <div style={{ whiteSpace: 'pre-wrap' }}>{diffText}</div>;
      },
    },
    {
      title: 'need_update',
      dataIndex: 'need_update',
      key: 'need_update',
      width: 110,
      render: (v) => {
        const num = Number(v);
        return num === 1 ? <Tag color="blue">是</Tag> : <Tag>否</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusColors = {
          success: 'green',
          failed: 'red',
          skipped: 'orange',
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: '时间',
      dataIndex: 'run_at',
      key: 'run_at',
      render: (timestamp) => (timestamp ? moment(timestamp).format('YYYY-MM-DD HH:mm:ss') : 'N/A'),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={2}>
          <BarChartOutlined style={{ marginRight: '8px' }} />
          交易记录
        </Title>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Select placeholder="选择实盘" style={{ width: 240 }} allowClear value={selectedFollow} onChange={handleFollowChange}>
            {follows.map((f) => {
              const name = f.alias || f.name || `实盘 ${f.id}`;
              return (
                <Option key={f.id} value={f.id}>
                  {name}
                </Option>
              );
            })}
          </Select>

          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={tradeRecords}
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />
    </Card>
  );
};

export default TradeRecords;
